import React, { useState, useEffect, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { formatInTimeZone } from 'date-fns-tz';

import { useBookingsContext } from '../../../contexts/BookingsContext';
import {
  StyledDateText,
  StyledDivider,
  StyledWrapper,
  useScheduleCardDetailsStyles,
} from './styles';
import DetailsItem from '../../../components/DetailsItem';
import CustomButton from '../../../components/CustomButton';
import InformationModal from '../../../components/InformationModal';
import { isValidIanaTimezone } from '../../../utils/helpers';

import { formatPostalCode, formatMobileNumber } from '../../../utils/helpers';
import { COLORS } from '../../../utils/colors';
import {
  BOOKING_STATUS,
  BOOKING_STATUS_LABELS,
} from '../../../global/constants/status';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import useSession from '../../../hooks/useSession';
import { MOBILE_STATUS_MESSAGES } from '../../../utils/messaging/mediaStatus';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
} from '@mui/material';
import { IMediaStatus } from '../../../global/interfaces';
import { IUser, IBookingAttachment } from '@homeporter/types';
import ViewAttachments from '../../../components/ViewAttachments';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
dayjs.extend(utc);

const formatData = (data: any, indent: string = ''): string => {
  return Object.entries(data)
    .map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return `${indent}${key}:\n${formatData(value, indent + '  ')}`;
      }
      return `${indent}${key}: ${value}`;
    })
    .join('\n');
};

const ScheduleCardDetails = ({ tab }: { tab?: string }) => {
  const navigate = useNavigate();
  const styles = useScheduleCardDetailsStyles();

  const {
    selectedBooking,
    setBookingListType,
    userSettings,
    description,
    yearBuilt,
    purchaseDate,
    context,
    setEditBooking,
    setIsBookingModalOpen,
    setBookingModalUrl,
    updateBookingStatus,
    getUser,
    refetchBookings,
  } = useBookingsContext();

  const { downloadSessionData, deleteSession } = useSession();

  const {
    sendSessionLink,
    isSendingSmsInvite,
    mediaStatus,
    setPingMediaStatus,
  } = useSession(selectedBooking?.booking_id);

  const [isSessionBlocked, setIsSessionBlocked] = useState(false);

  const [selectedAttachmentIndex, setSelectedAttachmentIndex] =
    useState<number>(-1);

  const isCompletedInspection = tab === 'archived';
  const bookingDate = selectedBooking?.booking_date;

  const redirectToVS = async () => {
    navigate(`inspector/${selectedBooking.booking_session_code}`);
  };

  const redirectToEditPage = async () => {
    console.log('redirect edit-report');
    navigate(`/edit-report/${selectedBooking.booking_id}`);
    //navigate('/vhc-pro')
  };

  const handleReschedule = () => {
    console.log(
      `load: ${window._env_?.BOOKING_APP_URL || process.env.REACT_APP_BOOKING_APP_URL}/reschedule/${selectedBooking?.booking_id}/${selectedBooking?.confirmation_code}?headless=1&dashboard=1`,
    );

    setBookingModalUrl(
      `${window._env_?.BOOKING_APP_URL || process.env.REACT_APP_BOOKING_APP_URL}/reschedule/${selectedBooking?.booking_id}/${selectedBooking?.confirmation_code}?headless=1&dashboard=1`,
    );
    setIsBookingModalOpen(true);
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationTitle, setConfirmationTitle] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [handleConfirmation, setHandleConfirmation] = useState<
    ((e: React.MouseEvent<HTMLButtonElement>) => void) | null
  >(null);

  const handleChangeBookingStatus = async (
    status: number,
    bookingId: number,
  ) => {
    if (!bookingId) return;

    setConfirmationTitle('Confirm Status Change');
    setConfirmationMessage(
      `The booking status will be updated to ${BOOKING_STATUS_LABELS[status]}.${
        status > BOOKING_STATUS.INSPECTION_COMPLETED
          ? ' Booking will be marked as archived.'
          : ''
      }`,
    );

    setHandleConfirmation((e: React.MouseEvent<HTMLButtonElement>) => {
      return async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        console.log('Updated function');
        try {
          const response = await updateBookingStatus?.({
            status,
            bookingId,
          });

          selectedBooking.status = response.status;
          setBookingListType(
            status > BOOKING_STATUS.INSPECTION_COMPLETED
              ? 'archived'
              : 'active',
          );
        } catch (error) {
          console.log('ERROR', error);
        }
      };
    });

    setConfirmationOpen(true);
  };

  const redeemedPromoCode =
    selectedBooking?.payment?.reference?.indexOf('promo-code-') === 0;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>('');

  const [modalPhase, setModalPhase] = useState<
    'confirm' | 'loading' | 'result'
  >('confirm');
  const [modalContent, setModalContent] = useState<string | null | JSX.Element>(
    '',
  );

  const handleClose = () => {
    setModalOpen(false);
    setModalPhase('confirm');
    setModalContent('');
  };

  const handleConnectionDetailsClick = () => {
    setPingMediaStatus(1);
    setModalTitle('Connection Details');
    setModalContent('');
    setModalOpen(true);
  };

  const toggleExpanded = (index: number) => {
    setMediaStatusList((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, expanded: !item.expanded } : item,
      ),
    );
  };

  const [mediaStatusList, setMediaStatusList] = useState<IMediaStatus[]>([]);
  useEffect(() => {
    if (mediaStatus) {
      setMediaStatusList(mediaStatus);
    }
  }, [mediaStatus]);

  useEffect(() => {
    if (modalOpen && modalTitle === 'Connection Details') {
      setModalContent(
        <div style={styles.mediaStatusList as CSSProperties}>
          {isSendingSmsInvite ? (
            <CircularProgress size={20} />
          ) : mediaStatusList && mediaStatusList.length > 0 ? (
            mediaStatusList.map((item, index) => (
              <React.Fragment key={index}>
                <Grid container spacing={2}>
                  <Grid item xs={8} sx={styles.mediaStatusItem}>
                    {item.data && (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(index)}
                        sx={styles.arrowIcon}>
                        {item.expanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    {`${MOBILE_STATUS_MESSAGES[item.event as keyof typeof MOBILE_STATUS_MESSAGES]?.message || item.event}`}
                  </Grid>
                  <Grid item xs={4}>
                    {dayjs(item.timestamp).format('YYYY/MM/DD HH:mm:ss')}
                  </Grid>
                </Grid>

                {item.expanded && item.data && (
                  <Grid item xs={12} sx={{ pl: '1.5rem', mb: '.75rem' }}>
                    <pre style={styles.pre as CSSProperties}>
                      {formatData(item.data)}
                    </pre>
                  </Grid>
                )}
              </React.Fragment>
            ))
          ) : (
            <div style={{ paddingLeft: '2.375rem' }}>
              No activity has been logged yet
            </div>
          )}
        </div>,
      );
    }
  }, [mediaStatusList, modalOpen, modalTitle, isSendingSmsInvite]);

  const [copyLink, setCopyLink] = useState(false);
  const handleMediaTestClick = async () => {
    setCopyLink(false);
    setModalTitle('Send Test Link');
    setModalContent(
      <Typography sx={{ px: '1.5rem', textAlign: 'center' }}>
        {selectedBooking?.mobile_number
          ? `To: ${formatMobileNumber(selectedBooking?.mobile_number)}`
          : `No mobile number found for ${selectedBooking?.payment?.user?.email}`}
      </Typography>,
    );

    setModalPhase('confirm');
    setModalOpen(true);
  };

  const handleSendTestLink = async () => {
    setModalPhase('loading');

    try {
      const response = await sendSessionLink({
        bookingSessionCode: selectedBooking.booking_session_code as string,
        type: 'test',
      });

      setModalPhase('result');
      setModalContent('Test link sent successfully!');
      setModalOpen(true);

      if (response.status === 'success')
        setModalContent('Test link sent successfully!');

      if (response.status === 'error') throw new Error(response.message);

      console.log('::::response:', response);
    } catch (error) {
      setModalPhase('result');
      setModalContent(
        <Typography>{String(error) || 'Failed to send test link.'}</Typography>,
      );
      console.error('Error sending test link:', error);
    }
  };

  const [sharedWith, setSharedWith] = useState<string[]>([]);

  useEffect(() => {
    const fetchSharedUsers = async () => {
      if (selectedBooking?.shared_with) {
        const emails = await Promise.all(
          selectedBooking.shared_with.map(async (user_id: number) => {
            const user = await getUser(user_id);
            return user?.email || 'fetching...';
          }),
        );
        setSharedWith(emails);
      }
    };
    fetchSharedUsers();
  }, [selectedBooking?.shared_with, getUser]);

  const archiveSession = async () => {
    console.log('::::archiveSession');
    const data = await downloadSessionData?.({
      bookingId: selectedBooking.booking_id,
      object: 'all',
    });
    setConfirmationOpen(false);

    if (data) {
      const fileName = `session-archive-${selectedBooking.booking_id}.zip`;

      const blob =
        data instanceof Blob
          ? data
          : new Blob([data], { type: 'application/zip' });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setTimeout(() => {
        setConfirmationOpen(true);
        setConfirmationTitle('Delete Session Data');
        setConfirmationMessage(
          `The session data has been downloaded, look for a file named "${fileName}" in your downloads folder.<br><br>Would you like to proceed to delete all session data? This action cannot be undone but you can restore the session data from the zip archive file.`,
        );
        setHandleConfirmation(() => {
          return async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            await deleteSession(selectedBooking.booking_id);
            await refetchBookings();
          };
        });
      }, 1000);
    }
  };

  return (
    <StyledWrapper onClick={(e) => e.stopPropagation()}>
      <Box sx={styles.cardHeader}>
        <Box display={'flex'}>
          <StyledDateText marginRight={'.75rem'}>
            {selectedBooking?.title || 'n/a'}
          </StyledDateText>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              md: 'column',
              lg: 'row',
            },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}>
          {selectedBooking?.status === BOOKING_STATUS.INSPECTION_COMPLETED && (
            <Button
              sx={{
                ...styles.baseButton,
                ...styles.rescheduleButton,
                padding: '.3755rem 2rem',
                whiteSpace: 'nowrap',
              }}
              onClick={redirectToEditPage}
              variant="outlined">
              Edit Report
            </Button>
          )}
          {selectedBooking?.status <= BOOKING_STATUS.INSPECTION_COMPLETED && (
            <CustomButton
              style={{ whiteSpace: 'nowrap' }}
              handleClick={redirectToVS}>
              Start Session
            </CustomButton>
          )}
        </Box>
      </Box>
      <Box sx={styles.cardOverflow}>
        {!isCompletedInspection && (
          <Box padding=".5rem .75rem">
            {false &&
              selectedBooking?.payment?.plan?.label !== 'spot session' && (
                <Button
                  disabled={
                    new Date().getTime() > new Date(bookingDate).getTime()
                  }
                  sx={{ ...styles.baseButton, ...styles.rescheduleButton }}
                  onClick={handleReschedule}
                  variant="text">
                  Reschedule
                </Button>
              )}
            <Box sx={styles.statusSelectBox}>
              <Typography sx={{ mr: 1 }}>Status:</Typography>
              {selectedBooking?.status === BOOKING_STATUS.EXPORTED ? (
                <Typography sx={{ mr: 1 }}>Exported</Typography>
              ) : (
                <Select
                  value={selectedBooking?.status || 1}
                  onChange={(e) =>
                    handleChangeBookingStatus(
                      e.target.value as number,
                      selectedBooking?.booking_id,
                    )
                  }
                  size="small"
                  variant="standard"
                  sx={styles.statusSelect}>
                  <MenuItem value={BOOKING_STATUS.OPEN}>
                    {BOOKING_STATUS_LABELS[BOOKING_STATUS.OPEN]}
                  </MenuItem>
                  <MenuItem value={BOOKING_STATUS.INSPECTION_COMPLETED}>
                    {BOOKING_STATUS_LABELS[BOOKING_STATUS.INSPECTION_COMPLETED]}
                  </MenuItem>
                  <MenuItem value={BOOKING_STATUS.REPORT_COMPLETED}>
                    {BOOKING_STATUS_LABELS[BOOKING_STATUS.REPORT_COMPLETED]}
                  </MenuItem>
                  <MenuItem value={BOOKING_STATUS.SESSION_CANCELED}>
                    {BOOKING_STATUS_LABELS[BOOKING_STATUS.SESSION_CANCELED]}
                  </MenuItem>
                </Select>
              )}
            </Box>

            <Button
              sx={{
                ...styles.baseButton,
                ...styles.rescheduleButton,
                float: 'right',
              }}
              onClick={() => {
                setEditBooking(null);
                setBookingModalUrl('booking-spot');
                setIsBookingModalOpen(true);
              }}
              variant="text">
              Edit
            </Button>
            <Button
              sx={{
                ...styles.baseButton,
                ...styles.rescheduleButton,
                float: 'right',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmationTitle('Export Session Data');
                setConfirmationMessage(
                  'This will download the the session data in a zip archive. Do you want to proceed?',
                );
                setHandleConfirmation(() => {
                  return async (
                    confirmEvent: React.MouseEvent<HTMLButtonElement>,
                  ) => {
                    confirmEvent.stopPropagation();
                    await archiveSession();
                  };
                });
                setConfirmationOpen(true);
              }}
              variant="text">
              Export
            </Button>
          </Box>
        )}
        {isCompletedInspection && (
          <>
            <Box padding=".5rem .75rem">
              {selectedBooking?.status !== BOOKING_STATUS.EXPORTED && (
                <Button
                  sx={{ ...styles.baseButton, ...styles.completeButton }}
                  onClick={() =>
                    handleChangeBookingStatus(
                      BOOKING_STATUS.OPEN,
                      selectedBooking?.booking_id,
                    )
                  }
                  variant="text">
                  Resume
                </Button>
              )}
            </Box>

            <Box sx={styles.statusSelectBox}>
              <Typography sx={{ mr: 1 }}>Status:</Typography>
              <Typography sx={{ mr: 1 }}>
                {BOOKING_STATUS_LABELS[selectedBooking?.status as number]}
              </Typography>
            </Box>
          </>
        )}

        <Box padding="1.3125rem 1.875rem">
          <Typography sx={styles.detailsHeading} color="#666e78">
            SESSION DETAILS
          </Typography>

          <Grid container alignSelf="center" ml="1rem" mt="0">
            <DetailsItem
              md={3}
              heading="SESSION TYPE"
              value={
                Object.keys(selectedBooking?.tasks || []).length
                  ? Object.keys(selectedBooking?.tasks).join(', ')
                  : selectedBooking?.payment?.plan.label
              }
            />
            <DetailsItem
              md={4}
              heading={`LOCAL TIME & DATE`}
              value={`${
                selectedBooking?.booking_date &&
                isValidIanaTimezone(selectedBooking?.timezone_name)
                  ? formatInTimeZone(
                      new Date(selectedBooking.booking_date),
                      selectedBooking?.timezone_name || 'UTC',
                      'MMM dd yyyy HH:mm (zzz)',
                    )
                  : 'n/a'
              }`}
            />
            <DetailsItem
              md={4}
              heading={`TIMEZONE`}
              value={`${selectedBooking?.timezone_name}`}
            />
            {userSettings?.dev && (
              <>
                <DetailsItem
                  md={3}
                  heading="DURATION"
                  value={`${selectedBooking?.session_duration} min` || ''}
                />

                <DetailsItem
                  md={4}
                  heading="BOOKING ID"
                  value={selectedBooking?.booking_id || ''}
                />
                <DetailsItem
                  md={4}
                  heading="CONFIRMATION CODE"
                  value={selectedBooking?.confirmation_code || ''}
                />
              </>
            )}
            {redeemedPromoCode && (
              <DetailsItem
                md={4}
                heading={
                  selectedBooking?.payment?.reference?.indexOf(
                    'promo-code-',
                  ) === 0
                    ? 'PROMO CODE'
                    : selectedBooking?.payment?.reference
                      ? 'PAYMENT REFERENCE'
                      : ''
                }
                value={
                  selectedBooking?.payment?.reference?.replace(
                    /promo-code-(.*)-[^-]+$/,
                    '$1',
                  ) || ''
                }
              />
            )}
            {userSettings?.dev && (
              <DetailsItem
                md={3}
                heading="MEDIA TEST"
                value={
                  <Button
                    variant="text"
                    onClick={handleMediaTestClick}
                    sx={{
                      margin: '0',
                      textDecoration: 'underline',
                      color: COLORS.MEDIUM_GRAY,
                      padding: '0',
                      minWidth: 'auto',
                      textTransform: 'none',
                      '&:hover': {
                        color: COLORS.PRIMARY,
                        backgroundColor: 'transparent',
                      },
                    }}>
                    send test link
                  </Button>
                }
              />
            )}

            {(selectedBooking?.attachments || []).length > 0 && (
              <DetailsItem
                md={12}
                heading="ATTACHMENTS"
                value={
                  <>
                    {selectedBooking.attachments.map(
                      (attachment: IBookingAttachment, index: number) => (
                        <Box key={index} sx={styles.file}>
                          <Typography
                            noWrap
                            sx={styles.filename}
                            title={attachment.name}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAttachmentIndex(index);
                            }}>
                            {attachment.name}
                          </Typography>
                        </Box>
                      ),
                    )}
                  </>
                }
              />
            )}

            <DetailsItem
              md={9}
              heading="CONNECTION DETAILS"
              value={
                <Button
                  variant="text"
                  sx={{
                    margin: '0',
                    textDecoration: 'underline',
                    color: COLORS.MEDIUM_GRAY,
                    padding: '0',
                    minWidth: 'auto',
                    textTransform: 'none',
                    '&:hover': {
                      color: COLORS.PRIMARY,
                      backgroundColor: 'transparent',
                    },
                  }}
                  onClick={handleConnectionDetailsClick}>
                  logs...
                </Button>
              }
            />
          </Grid>
        </Box>

        <StyledDivider />

        <Box padding="1.3125rem 1.875rem">
          <Typography sx={styles.detailsHeading} color="#666e78">
            CONTACT DETAILS
          </Typography>

          <Grid container alignSelf="center" ml="1rem" mt="0">
            <DetailsItem
              md={6}
              heading="NAME"
              value={selectedBooking?.title || ''}
            />
            <DetailsItem
              md={6}
              heading="EMAIL"
              value={selectedBooking?.payment?.user?.email}
            />
            <DetailsItem
              md={6}
              heading="NUMBER"
              value={
                selectedBooking?.mobile_number
                  ? `${formatMobileNumber(selectedBooking?.mobile_number)}`
                  : ''
              }
            />
            {(selectedBooking?.address ||
              selectedBooking?.city ||
              selectedBooking?.province ||
              selectedBooking?.postal_code) && (
              <DetailsItem
                md={6}
                heading="ADDRESS"
                value={
                  `${selectedBooking?.address}\\\\n${selectedBooking?.city}${
                    selectedBooking?.city && selectedBooking?.province
                      ? ', '
                      : ''
                  }${selectedBooking?.province}\\\\n${formatPostalCode(
                    selectedBooking?.postal_code,
                  )}` || ''
                }
              />
            )}
          </Grid>
        </Box>

        {(!selectedBooking.owner ||
          (selectedBooking?.shared_with || []).length > 0) && (
          <>
            <StyledDivider />
            <Box padding="1.3125rem 1.875rem">
              <Typography sx={styles.detailsHeading} color="#666e78">
                SHARE
              </Typography>

              <Grid container alignSelf="center" ml="1rem" mt="0">
                {!selectedBooking?.owner && (
                  <DetailsItem
                    md={6}
                    heading="OWNER"
                    value={selectedBooking?.inspector_owner?.email || 'n/a'}
                  />
                )}
                {selectedBooking?.shared_with?.length > 0 && (
                  <DetailsItem
                    md={6}
                    heading="SHARED WITH"
                    value={sharedWith.join(', ')}
                  />
                )}
              </Grid>
            </Box>
          </>
        )}

        {description && (
          <>
            <StyledDivider />
            <Box padding="1.3125rem 1.875rem">
              <Typography sx={styles.detailsHeading} color="#666e78">
                NOTES
              </Typography>
              <Grid container alignSelf="center" my={'1rem'} ml="1rem">
                <Grid item md={11}>
                  <Typography
                    width="85%"
                    sx={{ wordWrap: 'break-word', whiteSpace: 'pre-line' }}
                    fontSize="0.875rem"
                    fontWeight={500}
                    color={COLORS.TEXT}>
                    {description || ''}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {false && (yearBuilt || purchaseDate || context) && (
          <>
            <StyledDivider />
            <Box padding="1.3125rem 1.875rem">
              {(yearBuilt || purchaseDate || context) && (
                <Grid container alignSelf="center" mt="0">
                  {yearBuilt && (
                    <DetailsItem heading="YEAR BUILT" value={yearBuilt} />
                  )}
                  {purchaseDate && (
                    <DetailsItem heading="PURCHASE DATE" value={purchaseDate} />
                  )}
                  {context && <DetailsItem heading="CONTEXT" value={context} />}
                </Grid>
              )}
            </Box>
          </>
        )}
      </Box>
      {isSessionBlocked ? (
        <InformationModal
          isModalOpen={isSessionBlocked}
          handleClose={() => setIsSessionBlocked(false)}
          message="This session is yet to begin. Please check again one hour prior to scheduled time."
        />
      ) : null}
      {selectedBooking?.attachments && (
        <ViewAttachments
          attachments={selectedBooking.attachments}
          selectedAttachmentIndex={selectedAttachmentIndex}
          setSelectedAttachmentIndex={setSelectedAttachmentIndex}
        />
      )}
      <Dialog
        PaperProps={{
          sx: { minWidth: 'min(40rem, 50vw)' },
        }}
        open={modalOpen}
        onClose={handleClose}>
        <DialogTitle>{modalTitle}</DialogTitle>
        <DialogContent id="modal-content" sx={{ m: 0, p: 0 }}>
          {modalContent}
        </DialogContent>
        <DialogActions>
          {modalTitle === 'Send Test Link' ? (
            <>
              <Button
                variant="text"
                sx={{ mr: 'auto' }}
                disabled={copyLink}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(
                    `${window._env_?.MOBILE_APP_URL || process.env.REACT_APP_MOBILE_APP_URL}/DeviceCheck/${selectedBooking?.guest_entry_code}`,
                  );
                  setCopyLink(true);
                }}>
                <ContentCopyIcon
                  sx={{
                    mr: '.5rem',
                    fontSize: '1rem',
                    color: COLORS.MEDIUM_GRAY,
                  }}
                />
                {copyLink ? 'Link Copied' : 'Copy Link'}
              </Button>
              <Button onClick={() => setModalOpen(false)}>Close</Button>
              <Button onClick={handleSendTestLink}>
                {modalPhase === 'loading' ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: '0.5rem' }} />
                    Sending...
                  </>
                ) : (
                  'Send'
                )}
              </Button>
            </>
          ) : (
            <Button onClick={() => setModalOpen(false)}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        title={confirmationTitle}
        message={confirmationMessage}
        onConfirm={async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          if (handleConfirmation) handleConfirmation(e);
          setConfirmationOpen(false);
        }}
      />
    </StyledWrapper>
  );
};

export default ScheduleCardDetails;
